import React from 'react';
import { motion } from 'framer-motion';
import { FiHeart, FiUsers, FiStar, FiClock, FiCheck, FiMapPin, FiArrowRight } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { SITE_URL, WHATSAPP } from '../config/seo-data';

const ease = [0.22, 1, 0.36, 1];

const services = [
  { icon: FiHeart, title: 'Bridal Car', desc: 'Decorated Mercedes-Benz for the couple. Ribbons, flowers, and champagne to make the journey unforgettable.' },
  { icon: FiUsers, title: 'Guest Shuttle', desc: 'Coordinated multi-vehicle transport between hotel, church, and reception venue. No one gets left behind.' },
  { icon: FiStar, title: 'Airport Pickups', desc: 'Welcome each guest personally as they arrive over multiple days. Name signs, luggage help, door-to-door.' },
  { icon: FiClock, title: 'Late-Night Returns', desc: 'Safe, reliable transport back to hotels and villas after the reception, no matter how late.' },
];

const included = [
  'Decorated Mercedes-Benz bridal vehicle',
  'English-speaking professional drivers',
  'Multi-vehicle coordination for guests',
  'Airport & port pickup for arriving guests',
  'Flexible timing around your schedule',
  'Pre-wedding event transport',
  'Child & booster seats on request',
  'Dedicated wedding day coordinator',
];

const venues = [
  { name: 'Corfu Old Town Churches', desc: 'Historic Venetian churches & the Cathedral' },
  { name: 'Achilleion Palace', desc: 'Empress Sisi\'s palace with Ionian Sea views' },
  { name: 'Daphnila Bay', desc: 'Luxury beachfront resort ceremonies' },
  { name: 'Mon Repos Estate', desc: 'Royal estate surrounded by gardens' },
  { name: 'Danilia Village', desc: 'Traditional Corfiot village setting' },
  { name: 'Private Villas', desc: 'Exclusive hilltop & seafront villa weddings' },
];

const WeddingTransfers = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Wedding Transfers & Transport"
    description="Luxury wedding transfers in Corfu. Decorated bridal car, guest shuttle service, airport pickups. Make your Corfu wedding transport perfect."
    keywords="corfu wedding transfer, corfu wedding transport, corfu wedding car hire, corfu wedding minibus, corfu wedding chauffeur"
    canonical={`${SITE_URL}/wedding-transfers`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/oldtown-street-vclass.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Your Special Day · Our Expertise
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Wedding
          <br />
          <span className="font-light italic">Transfers</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-white/85 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          From airport guest pickups to decorated bridal vehicles, we handle every detail of your Corfu wedding transport.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
        >
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hello! I would like to discuss wedding transfer arrangements in Corfu.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Discuss Your Wedding
          </a>
        </motion.div>
      </div>
    </section>

    {/* Services */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Our Services</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Every Detail <span className="italic">Covered</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
              className="group bg-[#faf7f6] border border-black/10 rounded-2xl p-8 shadow-sm hover:bg-white hover:border-[#ba1609]/30 hover:shadow-lg transition-[color,background-color,border-color,box-shadow] duration-500 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#ba1609]/10 border border-[#ba1609]/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#ba1609]/20 transition-all duration-500">
                <s.icon className="w-6 h-6 text-[#ba1609]" />
              </div>
              <h3 className="text-[#1a1012] text-lg font-medium mb-3">{s.title}</h3>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* What's Included + Why Us */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Everything Included</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              A Seamless
              <br /><span className="italic">Wedding Experience</span>
            </h2>
            <div className="space-y-4">
              {included.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-[#ba1609]/10 border border-[#ba1609]/20 flex items-center justify-center shrink-0">
                    <FiCheck className="w-3 h-3 text-[#ba1609]" />
                  </div>
                  <span className="text-[#5b4d4f] text-sm font-light">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="bg-white border border-black/10 rounded-2xl p-8 lg:p-10 shadow-sm"
          >
            <h3 className="text-[#1a1012] text-xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why Choose Us for Your Wedding?
            </h3>
            <div className="w-10 h-[1px] bg-[#ba1609] mb-6" />
            <div className="space-y-4 text-[#5b4d4f] text-sm leading-relaxed font-light">
              <p>A Corfu wedding involves a lot of moving parts, guests arriving from different airports, transfers between hotels and venues, and late-night returns. One missed pickup can cascade through your entire timeline.</p>
              <p>We assign a dedicated coordinator to your wedding. They plan every route, track every flight, and make sure your guests are where they need to be, on time, every time.</p>
              <p>Our drivers are experienced with Corfu's top wedding venues and know the fastest, smoothest routes to get there. No narrow-road surprises on your big day.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Venues */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">We Know Every Venue</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Popular Wedding <span className="italic">Venues</span>
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {venues.map((v, i) => (
            <motion.div
              key={v.name}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06, ease }}
              className="group bg-[#faf7f6] border border-black/10 rounded-xl px-6 py-5 shadow-sm hover:bg-white hover:border-[#ba1609]/30 hover:shadow-lg transition-[color,background-color,border-color,box-shadow] duration-300"
            >
              <div className="flex items-start gap-3">
                <FiMapPin className="w-4 h-4 text-[#ba1609] mt-1 shrink-0" />
                <div>
                  <h3 className="text-[#1a1012] font-medium mb-1">{v.name}</h3>
                  <p className="text-[#5b4d4f] text-xs font-light">{v.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="py-24 bg-[#ba1609]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
        >
          <p className="text-white/80 text-xs uppercase tracking-[0.4em] mb-4">Let's Plan Together</p>
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Perfect Day <span className="italic">Starts Here</span>
          </h2>
          <p className="text-white/85 text-sm font-light mb-10 max-w-lg mx-auto leading-relaxed">
            Share your wedding details and guest count. We'll design a complete transport plan tailored to your celebration.
          </p>
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hello! I would like to discuss wedding transfer arrangements in Corfu.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-[#ba1609] px-10 py-4 rounded-full font-medium hover:bg-[#fbeceb] transition-all duration-300 text-base tracking-wide"
          >
            Plan My Wedding Transport
          </a>
        </motion.div>
      </div>
    </section>
  </SEOPageLayout>
);

export default WeddingTransfers;
